import React, { useEffect, useMemo, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import Chart from "chart.js/auto";
import { FormatIndianNumber } from "../utiles/FormatIndianNumber";
import { useDispatch, useSelector } from "react-redux";
import {
  setInvestmentDataSIP,
  setFilterInvestmentData,
  setSelectedOption,
} from "../redux/calculations/slice";
import {
  setSelectedOptionLum,
  setFilterInvestmentDataLum,
  setInvestmentDataLUM,
} from "../redux/calculations/lumpsumSlice";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import PensionBar from "../charts/PensionBar";
import CarBarChart from "../charts/CarBarChart";
import ContributionChart from "../charts/ContributionChart";
import MortgageChart from "../charts/MortgageChart";
import useLocale from "../../src/locales/index";
import { useParams } from "react-router-dom";
import { formatNumberShort } from "../utiles/langNumber"
import i18n from "../i18n";

const ChartComponent = React.memo(
  ({ activeTab, setShowTable, calcDone, activeCalculator }) => {
    const { calculatorType } = useParams();
    const langCode = i18n.language || "default";

    // Mapping between URL paths and calculator IDs
    const pathToCalculatorMap = {
      'sip-investment-calculator': 'sipCalc',
      'personal-loan-calculator': 'personalLoanCalc',
      'fixed-deposit-calculator': 'FDCalc',
      'recurring-deposit-calculator': 'RDCalc',
      'debt-to-income-ratio-calculator': 'DTICalc',
      'net-worth-calculator': 'netWorthCalc',
      'loan-payoff-calculator': 'earlyLoanCalc',
      'student-education-loan-calculator': 'educationLoanCalc',
      'credit-card-payoff-calculator': 'creditCardPayoffCalc',
      'business-loan-calculator': 'businessLoanCalc',
      'gst-tax-calculator': 'GSTCalc',
      'salary-calculator': 'salaryCalc',
      'pension-calculator': 'pensionCalc',
      'car-lease-calculator': 'carCalc',
      'mortgage-loan-refinance-calculator': 'mortgageCalc',
      '401k-retirement-calculator': 'contributionCalc',
      'saving-goal-calculator': 'savingCalc',
      'loan-to-value-calculator': 'loanToValueCalc'
    };

    // Get the calculator ID from the URL path
    const calculatorId = pathToCalculatorMap[calculatorType] || calculatorType;

    const dropdownRef = useRef(null);
    const cookieAccept = localStorage.getItem("grow-cookies");
    const { textData } = useLocale();
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);

    const { chartData } = useSelector((state) => state.calculator);
    const carData = useSelector((state) => state.calculator.carData);
    const { emi } = useSelector((state) => state.educationCalculator);
    const { perEmi } = useSelector((state) => state.personalLoanCalculator);
    const { totalValue } = useSelector((state) => state.rdCalculator);
    const { sumAmount } = useSelector((state) => state.fdCalculator);
    const { bussinessEmi } = useSelector((state) => state.bussinessCalculator);
    const { monthsToPayoff } = useSelector((state) => state.creditCalculator);
    const { newEmi } = useSelector((state) => state.earlyLoanCalculator);
    const { dtiRatio } = useSelector((state) => state.dtiCalculator);
    const { netWorth } = useSelector((state) => state.networthCalculator);
    const { gstValue } = useSelector((state) => state.gstCalculator);
    const { vatValue } = useSelector((state) => state.vatCalculator);
    const { annualSalary } = useSelector((state) => state.salaryCalculator);
    const { totalWorkingHours } = useSelector(
      (state) => state.salaryCalculator
    );
    const { monthlyPension } = useSelector((state) => state.pensionCalculator);
    const { monthlyEmi } = useSelector((state) => state.ltvCalculator);
    const { monthlyInvestment } = useSelector(
      (state) => state.savingsCalculator
    );
    const { futureValueOfGoal } = useSelector(
      (state) => state.savingsCalculator
    );
    const { newAmount } = useSelector((state) => state.mortgageCalculator);

    const { futureValueOfExistingInvestment } = useSelector(
      (state) => state.savingsCalculator
    );
    const { monthlyRate } = useSelector(
      (state) => state.contributionCalculator
    );
    useEffect(() => { }, [calculatorType, chartData]);

    const {
      investmentDataSIP,
      selectedOption,
      filterInvestmentData,
      noOfInvestementYear,
    } = useSelector((state) => (activeTab === 1 ? state.lum : state.sip));

    const shouldRenderChart = calculatorId !== "salaryCalc";

    const pensionResult = useSelector((state) => {
      if (state.calculator.pensionData) {
        return state.calculator.pensionData;
      }
    });
    const isRTL = () => {
      const rtlLanguages = ["ar", "fa", "ur"];
      return rtlLanguages.includes(i18n.language);
    };

    const shouldRenderBarChart =
      calculatorId === "pensionCalc" &&
      chartData["pensionCalc"] &&
      chartData["pensionCalc"].length > 0;

    const shouldRenderCarBarChart =
      calculatorId === "carCalc" &&
      chartData["carCalc"] &&
      chartData["carCalc"].length > 0;

    const shouldRenderContributionBarChart =
      calculatorId === "contributionCalc" &&
      chartData["contributionCalc"] &&
      chartData["contributionCalc"].length > 0;

    const showDurationDropdown = ["sipCalc", "lumpsumCalc"].includes(
      calculatorType
    );

    useEffect(() => {
      if (calcDone) {
        if (selectedOption === textData.All) {
          dispatch(
            activeTab === 0
              ? setFilterInvestmentData(investmentDataSIP)
              : setFilterInvestmentDataLum(investmentDataSIP)
          );
          dispatch(
            activeTab === 0
              ? setInvestmentDataSIP(investmentDataSIP)
              : setInvestmentDataLUM(investmentDataSIP)
          );
        } else if (selectedOption === `0-${noOfInvestementYear}`) {
          const filteredData = investmentDataSIP.filter(
            (item) => item.year <= noOfInvestementYear
          );
          dispatch(
            activeTab === 0
              ? setFilterInvestmentData(filteredData)
              : setFilterInvestmentDataLum(filteredData)
          );
        } else if (selectedOption === "0-30") {
          const filteredData = investmentDataSIP.filter(
            (item) => item.year <= 30
          );
          dispatch(
            activeTab === 0
              ? setFilterInvestmentData(filteredData)
              : setFilterInvestmentDataLum(filteredData)
          );
        }
      }
    }, [investmentDataSIP, selectedOption, calcDone]);

    const handleOptionClick = (option) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "filter_report");
      }
      // cookieAccept && logEvent(analytics, `filter_report`);
      dispatch(
        activeTab === 0
          ? setSelectedOption(option)
          : setSelectedOptionLum(option)
      );

      if (option === textData.All) {
        dispatch(
          activeTab === 0
            ? setFilterInvestmentData(investmentDataSIP)
            : setFilterInvestmentDataLum(investmentDataSIP)
        );
        dispatch(
          activeTab === 0
            ? setInvestmentDataSIP(investmentDataSIP)
            : setInvestmentDataLUM(investmentDataSIP)
        );
      } else if (option === `0-${noOfInvestementYear}`) {
        const filteredData = investmentDataSIP.filter(
          (item) => item.year <= noOfInvestementYear
        );
        dispatch(
          activeTab === 0
            ? setFilterInvestmentData(filteredData)
            : setFilterInvestmentDataLum(filteredData)
        );
      } else if (option === "0-30") {
        const filteredData = investmentDataSIP.filter(
          (item) => item.year <= 30
        );
        dispatch(
          activeTab === 0
            ? setFilterInvestmentData(filteredData)
            : setFilterInvestmentDataLum(filteredData)
        );
      }
      setShowTable(false);
      setIsOpen(false);
    };

    const toggleDropdown = () => {
      setIsOpen(!isOpen);
    };

    useEffect(() => {
      if (noOfInvestementYear > 0) {
        dispatch(
          activeTab === 0
            ? setSelectedOption(`${textData.All}`)
            : setSelectedOptionLum(`${textData.All}`)
        );
        if (calcDone) {
          const filteredData = investmentDataSIP.filter(
            (item) => item.year <= noOfInvestementYear
          );
          dispatch(
            activeTab === 0
              ? setFilterInvestmentData(filteredData)
              : setFilterInvestmentDataLum(filteredData)
          );
        }
      }
    }, [noOfInvestementYear]);

    const generateDropdownOptions = (noOfInvestementYear) => {
      const options = [];
      if (noOfInvestementYear !== 30) {
        const maxOption = Math.min(noOfInvestementYear, 46);
        options.push({ label: `0-${maxOption}`, value: `0-${maxOption}` });
      }
      options.push({ label: "0-30", value: "0-30" });
      options.push({ label: textData.All, value: textData.All });
      return options;
    };

    function getLastTotalInvestment(data) {
      for (let i = data?.length - 1; i >= 0; i--) {
        const totalInvestment = data[i]?.totalInvestment;
        if (typeof totalInvestment === "number") {
          return totalInvestment;
        }
      }
      return null;
    }

    const fData =
      filterInvestmentData?.length > 0
        ? filterInvestmentData
        : investmentDataSIP;

    const totalInvestment = getLastTotalInvestment(fData);

    const yearlyReturnTotal = fData[fData.length - 1]?.yearlyReturn;

    const yearlyReturnAfterSWPTotal =
      fData[fData.length - 1]?.yearlyReturnAfterSWP;

    const totalWithDrawal = fData
      ?.map((item) => item.swpAmount)
      ?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);

    const getChartData = () => {
      const colors = {
        ["sipCalc"]: ["#57C6F4", "#FFC566", "#FF8566", "#76C576"],
        ["lumpsumCalc"]: ["#57C6F4", "#FFC566", "#FF8566", "#76C576"],
        ["personalLoanCalc"]: ["#57C6F4", "#FF8566"],
        ["FDCalc"]: ["#57C6F4", "#FF8566"],
        ["RDCalc"]: ["#57C6F4", "#FF8566"],
        ["educationLoanCalc"]: ["#57C6F4", "#FF8566"],
        ["creditCardPayoffCalc"]: ["#57C6F4", "#FF8566"],
        ["businessLoanCalc"]: ["#57C6F4", "#FF8566"],
        ["earlyLoanCalc"]: ["#57C6F4", "#FF8566"],
        ["DTICalc"]: ["#57C6F4", "#FF8566"],
        ["netWorthCalc"]: ["#57C6F4", "#FF8566"],
        ["GSTCalc"]: ["#57C6F4", "#FF8566"],
        ["VatCalc"]: ["#57C6F4", "#FF8566"],
        ["salaryCalc"]: ["#57C6F4", "#FF8566"],
        ["pensionCalc"]: ["#57C6F4", "#FF8566"],
        ["loanToValueCalc"]: ["#57C6F4", "#FF8566"],
        ["savingCalc"]: "",
        ["carCalc"]: "",
        ["contributionCalc"]: "",
        ["mortgageCalc"]: "",
      };

      const defaultData = {
        labels: [
          textData.chartLabels.totalInvestment,
          textData.chartLabels.estimateReturn,
        ],
        datasets: [
          {
            label: ["0", "0"],
            data: [50, 50],
            backgroundColor: ["#57C6F4", "#FF8566"],
            spacing: 5,
            cutout: "55%",
          },
        ],
      };
      const getDefaultData = (
        labels = [
          textData.chartLabels.totalInvestment,
          textData.chartLabels.estimateReturn,
        ]
      ) => {
        return {
          labels: labels,
          datasets: [
            {
              label: ["0", "0"],
              data: [50, 50],
              backgroundColor: ["#57C6F4", "#FF8566"],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      };

      const defaultDataSipLumpsum = {
        labels: [
          textData.chartLabels.totalInvestment,
          textData.chartLabels.estimateReturn,
          textData.chartLabels.totalWithrawal,
          textData.chartLabels.estReturn,
        ],
        datasets: [
          {
            label: ["0", "0", "0", "0"],
            data: [25, 25, 25, 25],
            backgroundColor: ["#57C6F4", "#FFC566", "#FF8566", "#76C576"],
            spacing: 5,
            cutout: "55%",
          },
        ],
      };

      if (calculatorId === "sipCalc") {
        const rawData = [
          {
            value: totalInvestment || 0,
            label: textData.chartLabels.totalInvestment,
            color: colors[calculatorId][0],
          },
          {
            value: yearlyReturnTotal || 0,
            label: textData.chartLabels.estimateReturn,
            color: colors[calculatorId][1],
          },
          {
            value: totalWithDrawal || 0,
            label: textData.chartLabels.totalWithrawal,
            color: colors[calculatorId][2],
          },
          {
            value: yearlyReturnAfterSWPTotal || 0,
            label: textData.chartLabels.estReturn,
            color: colors[calculatorId][3],
          },
        ];

        // Filter out negative values and zero values
        const filteredData = rawData.filter((item) => item.value > 0);

        // If no positive values, return default
        if (filteredData.length === 0) {
          return defaultDataSipLumpsum;
        }

        return {
          labels: filteredData.map((item) => item.label),
          datasets: [
            {
              label: filteredData.map((item) => FormatIndianNumber(item.value)),
              data: filteredData.map((item) => item.value),
              backgroundColor: filteredData.map((item) => item.color),
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "lumpsumCalc") {
        const rawData = [
          {
            value: totalInvestment || 0,
            label: textData.chartLabels.totalInvestment,
            color: colors[calculatorType][0],
          },
          {
            value: yearlyReturnTotal || 0,
            label: textData.chartLabels.estimateReturn,
            color: colors[calculatorType][1],
          },
          {
            value: totalWithDrawal || 0,
            label: textData.chartLabels.totalWithrawal,
            color: colors[calculatorType][2],
          },
          {
            value: yearlyReturnAfterSWPTotal || 0,
            label: textData.chartLabels.estReturn,
            color: colors[calculatorType][3],
          },
        ];

        // Filter out negative values and zero values
        const filteredData = rawData.filter((item) => item.value > 0);

        // If no positive values, return default
        if (filteredData.length === 0) {
          return defaultDataSipLumpsum;
        }

        return {
          labels: filteredData.map((item) => item.label),
          datasets: [
            {
              label: filteredData.map((item) => FormatIndianNumber(item.value)),
              data: filteredData.map((item) => item.value),
              backgroundColor: filteredData.map((item) => item.color),
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "personalLoanCalc") {
        const personalLoandata = chartData["personalLoanCalc"];
        const filteredData = personalLoandata?.slice(0, 2);
        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ],
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["personalLoanCalc"] || [
                "#57C6F4",
                "#FF8566",
              ],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "FDCalc") {
        const fdData = chartData["FDCalc"];
        const filteredData = fdData?.slice(0, 2);
        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.totalInvestment,
            textData.chartLabels.estimateReturn,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.totalInvestment,
            textData.chartLabels.estimateReturn,
          ]);
        }

        return {
          labels: [
            textData.chartLabels.totalInvestment,
            textData.chartLabels.estimateReturn,
          ],
          datasets: [
            {
              label: fdData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["FDCalc"] || [
                "#57C6F4",
                "#FFC566",
                "#FF8566",
              ],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "RDCalc") {
        const rdData = chartData["RDCalc"];
        const filteredData = rdData?.slice(0, 2);

        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.totalInvestment,
            textData.chartLabels.estimateReturn,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.totalInvestment,
            textData.chartLabels.estimateReturn,
          ]);
        }

        return {
          labels: [
            textData.chartLabels.totalInvestment,
            textData.chartLabels.estimateReturn,
          ],
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["RDCalc"] || ["#57C6F4", "#FF8566"],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "educationLoanCalc") {
        const educationLoanData = chartData["educationLoanCalc"];
        const filteredData = educationLoanData?.slice(0, 2);
        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ]);
        }

        return {
          labels: [
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ],
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["educationLoanCalc"] || [
                "#57C6F4",
                "#FF8566",
              ],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "creditCardPayoffCalc") {
        const creditData = chartData["creditCardPayoffCalc"];
        const filteredData = creditData?.slice(0, 2);

        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.loanAmount,
            textData.chartLabels.intrestAmount,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.loanAmount,
            textData.chartLabels.intrestAmount,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.loanAmount,
            textData.chartLabels.intrestAmount,
          ],
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["creditCardPayoffCalc"] || [
                "#57C6F4",
                "#FF8566",
              ],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "businessLoanCalc") {
        const bussinessData = chartData["businessLoanCalc"];
        const filteredData = bussinessData?.slice(0, 2);
        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ],
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["businessLoanCalc"] || [
                "#57C6F4",
                "#FF8566",
              ],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "earlyLoanCalc") {
        const earlyData = chartData["earlyLoanCalc"];
        const filterData = earlyData?.slice(0, 2);

        const hasValues = filterData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.totalIntrest,
          ]);
        }
        const actualData = filterData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.totalIntrest,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.principle,
            textData.chartLabels.totalIntrest,
            textData.chartLabels.remainingPrinciple,
          ],
          datasets: [
            {
              label: earlyData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["earlyLoanCalc"] || [
                "#57C6F4",
                "#FF8566",
              ],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "DTICalc") {
        const dtiData = chartData["DTICalc"];

        const hasValues = dtiData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.monthlyDebt,
            textData.chartLabels.remainingIncome,
          ]);
        }
        const actualData = dtiData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.monthlyDebt,
            textData.chartLabels.remainingIncome,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.monthlyDebt,
            textData.chartLabels.remainingIncome,
          ],
          datasets: [
            {
              label: dtiData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["DTICalc"] || ["#57C6F4", "#FF8566"],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "netWorthCalc") {
        const networthData = chartData["netWorthCalc"];

        const hasValues = networthData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.totalAssets,
            textData.chartLabels.totalLiabilities,
          ]);
        }
        const actualData = networthData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.totalAssets,
            textData.chartLabels.totalLiabilities,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.totalAssets,
            textData.chartLabels.totalLiabilities,
          ],
          datasets: [
            {
              label: networthData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["DTICalc"] || ["#57C6F4", "#FF8566"],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (activeCalculator === "GSTCalc") {
        const gstData = chartData["GSTCalc"];
        const filteredData = gstData?.slice(0, 2);

        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.gstAMoount,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.gstAMoount,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.principle,
            textData.chartLabels.gstAMoount,
          ],
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["DTICalc"] || ["#57C6F4", "#FF8566"],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (activeCalculator === "VatCalc") {
        const vatData = chartData["VatCalc"];
        if (!vatData || vatData.length === 0) return null;

        const filteredData = vatData?.slice(0, 2);

        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.vatAmount,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.vatAmount,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.principle,
            textData.chartLabels.vatAmount,
          ],
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["VatCalc"] || ["#57C6F4", "#FF8566"],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "salaryCalc") {
        return {
          labels: [],
          datasets: [],
        };
      } else if (calculatorId === "pensionCalc") {
        const pensionData = chartData["pensionCalc"];
        const hasValues = pensionData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.annuityValue,
            textData.chartLabels.withDrawAmount,
          ]);
        }
        const actualData = pensionData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.annuityValue,
            textData.chartLabels.withDrawAmount,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.annuityValue,
            textData.chartLabels.withDrawAmount,
          ],
          datasets: [
            {
              label: pensionData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["pensionCalc"] || ["#57C6F4", "#FF8566"],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "loanToValueCalc") {
        const ltvData = chartData["loanToValueCalc"];
        const filteredData = ltvData?.slice(0, 2);

        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ]);
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return getDefaultData([
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ]);
        }
        return {
          labels: [
            textData.chartLabels.principle,
            textData.chartLabels.intrestAmount,
          ],
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["loanToValueCalc"] || [
                "#57C6F4",
                "#FF8566",
              ],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "savingCalc") {
        return {
          labels: [],
          datasets: [],
        };
      } else if (calculatorId === "carCalc") {
        return {
          labels: [],
          datasets: [],
        };
      } else if (calculatorId === "contributionCalc") {
        const contributionData = chartData["contributionCalc"];
        const filteredData = contributionData?.slice(0, 2);
        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return defaultData;
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return defaultData;
        }
        return {
          labels: filteredData.map((item) => item.label),
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["contributionCalc"] || [
                "#57C6F4",
                "#FF8566",
              ],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      } else if (calculatorId === "mortgageCalc") {
        const mortgageData = chartData["mortgageCalc"];
        const filteredData = mortgageData?.slice(0, 2);
        const hasValues = filteredData?.some(
          (item) => item.value && parseFloat(item.value) > 0
        );
        if (!hasValues) {
          return defaultData;
        }
        const actualData = filteredData.map((item) =>
          item.value ? parseFloat(item.value) : 0
        );

        const allZero = actualData.every((value) => value === 0);
        if (allZero) {
          return defaultData;
        }
        return {
          labels: filteredData.map((item) => item.label),
          datasets: [
            {
              label: filteredData.map((item) =>
                typeof item.value === "number" || !isNaN(parseFloat(item.value))
                  ? FormatIndianNumber(parseFloat(item.value))
                  : "0"
              ),
              data: actualData,
              backgroundColor: colors["mortgageCalc"] || ["#57C6F4", "#FF8566"],
              spacing: 5,
              cutout: "55%",
            },
          ],
        };
      }
    };

    const data = useMemo(
      () => getChartData(),
      [
        shouldRenderChart,
        calculatorType,
        totalInvestment,
        yearlyReturnTotal,
        yearlyReturnAfterSWPTotal,
        totalWithDrawal,
        investmentDataSIP,
        chartData,
        shouldRenderBarChart,
        gstValue,
        vatValue,
        totalValue,
        sumAmount,
        newAmount,
        bussinessEmi,
        monthsToPayoff,
        newEmi,
        dtiRatio,
        netWorth,
        annualSalary,
        totalWorkingHours,
        monthlyPension,
        carData,
        shouldRenderContributionBarChart,
      ]
    );

    const options = {
      plugins: {
        legend: {
          display: false,
          position: "bottom",
        },
        tooltip: {
          enabled: false, // Disable default tooltip
          external: function (context) {
            // External tooltip implementation
            const { chart, tooltip } = context;
            let tooltipEl = document.getElementById("chartjs-tooltip");

            // Create element if it doesn't exist
            if (!tooltipEl) {
              tooltipEl = document.createElement("div");
              tooltipEl.id = "chartjs-tooltip";
              tooltipEl.innerHTML = "<div></div>";
              document.body.appendChild(tooltipEl);
            }

            // Hide if no tooltip
            if (tooltip.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            }

            // Set text
            if (tooltip.body) {
              const index = tooltip.dataPoints[0].dataIndex;
              const label = data.labels[index];
              const value = tooltip.dataPoints[0].dataset.label[index];

              // Create tooltip content with inline styles instead of Tailwind classes
              const innerHtml = `
              <div style="padding: 12px; background-color: white; border-radius: 6px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); border: 1px solid #f1f1f1; min-width: 120px;">
                <div style="font-size: 14px; font-weight: normal; color: #282828; margin-bottom: 4px;">${label}</div>
                <div style="font-size: 13px; font-weight: 600; color: #282828;">${textData.currency} ${value}</div>
              </div>
            `;

              const tableRoot = tooltipEl.querySelector("div");
              tableRoot.innerHTML = innerHtml;
            }

            // Position tooltip
            const position = chart.canvas.getBoundingClientRect();

            // Apply all styles inline to the tooltip element
            Object.assign(tooltipEl.style, {
              opacity: "1",
              position: "absolute",
              left: position.left + window.pageXOffset + tooltip.caretX + "px",
              top: position.top + window.pageYOffset + tooltip.caretY + "px",
              pointerEvents: "none",
              transition: "all .1s ease",
              transform: "translate(-50%, -100%)",
              zIndex: "1000",
              fontFamily: "inherit",
            });
          },
        },
      },
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const renderChartLegends = () => {
      if (!shouldRenderChart) return null;
      if (calculatorId === "sipCalc" || calculatorId === "lumpsumCalc") {
        return (
          <>
            <div className="flex justify-between items-center mt-[40px]">
              <div className="flex items-center">
                <div
                  className={`bg-[#57C6F4] h-[20px] w-[20px] rounded-[4px] ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                    } `}
                ></div>
                <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                  {textData.chartLabels.totalInvestment}
                </div>
              </div>
              <div className="text-sm sm:text-sm text-black font-medium">
                {textData.currency}
                {totalInvestment ? FormatIndianNumber(totalInvestment) : 0}
              </div>
            </div>
            <div className="flex justify-between mt-[22px]">
              <div className="flex items-center">
                <div
                  className={`bg-[#FFC566] h-[20px] w-[20px] rounded-[4px] ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                    }`}
                ></div>
                <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                  {textData.chartLabels.estimateReturn}
                </div>
              </div>
              <div className="text-sm sm:text-sm text-black font-medium">
                {textData.currency}
                {yearlyReturnTotal ? FormatIndianNumber(yearlyReturnTotal) : 0}
              </div>
            </div>
            <div className="flex justify-between mt-[22px]">
              <div className="flex items-center">
                <div
                  className={`bg-[#FF8566] h-[20px] w-[20px] rounded-[4px] ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                    }`}
                ></div>
                <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                  {textData.chartLabels.totalWithrawal}
                </div>
              </div>
              <div className="text-sm sm:text-sm text-black font-medium">
                {textData.currency}
                {totalWithDrawal ? FormatIndianNumber(totalWithDrawal) : 0}
              </div>
            </div>
            <div className="flex justify-between mt-[22px]">
              <div className="flex items-center">
                <div
                  className={`bg-[#76C576] h-[20px] w-[20px] rounded-[4px] ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                    }`}
                ></div>
                <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                  {textData.chartLabels.estReturn}
                </div>
              </div>
              <div className="text-sm sm:text-sm text-black font-medium">
                {textData.currency}
                {yearlyReturnAfterSWPTotal
                  ? FormatIndianNumber(yearlyReturnAfterSWPTotal)
                  : 0}
              </div>
            </div>
          </>
        );
      } else if (calculatorId === "personalLoanCalc") {
        const personalLoanData = chartData["personalLoanCalc"] || [];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {personalLoanData.map((item, index) => {
              const isLastItem = index === personalLoanData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {" "}
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "FDCalc") {
        const fdData = chartData["FDCalc"] || [];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {fdData?.map((item, index) => {
              const isLastItem = index === fdData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "RDCalc") {
        const rdData = chartData["RDCalc"] || [];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {rdData?.map((item, index) => {
              const isLastItem = index === rdData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "educationLoanCalc") {
        const educationLoanData = chartData["educationLoanCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {educationLoanData?.map((item, index) => {
              const isLastItem = index === educationLoanData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-lato-medium font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "creditCardPayoffCalc") {
        const colors = ["#57C6F4", "#FF8566"];
        const creditData = chartData["creditCardPayoffCalc"];

        return (
          <>
            {creditData?.map((item, index) => {
              const isLastItem = index === creditData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "businessLoanCalc") {
        const bussinessData = chartData["businessLoanCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {bussinessData?.map((item, index) => {
              const isLastItem = index === bussinessData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "earlyLoanCalc") {
        const earlyData = chartData["earlyLoanCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {earlyData?.map((item, index) => {
              const isLastItem = index === earlyData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "DTICalc") {
        const dtiData = chartData["DTICalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {dtiData?.map((item, index) => {
              return (
                <React.Fragment key={item.label}>
                  <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0">
                    <div className="flex items-center">
                      <div
                        className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                          }`}
                        style={{ backgroundColor: colors[index] }}
                      ></div>
                      <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                        {textData["chartLabels"][item.label]}
                      </div>
                    </div>

                    <div className="text-sm sm:text-sm text-black font-medium">
                      {textData.currency}{" "}
                      {typeof item.value === "number" ||
                        !isNaN(parseFloat(item.value))
                        ? FormatIndianNumber(parseFloat(item.value))
                        : "0"}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "netWorthCalc") {
        const netWorthData = chartData["netWorthCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {netWorthData?.map((item, index) => {
              return (
                <React.Fragment key={item.label}>
                  <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0 last:pb-0">
                    <div className="flex items-center">
                      <div
                        className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                          }`}
                        style={{ backgroundColor: colors[index] }}
                      ></div>
                      <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                        {textData["chartLabels"][item.label]}
                      </div>
                    </div>

                    <div className="text-sm sm:text-sm text-black font-medium">
                      {textData.currency}{" "}
                      {typeof item.value === "number" ||
                        !isNaN(parseFloat(item.value))
                        ? FormatIndianNumber(parseFloat(item.value))
                        : "0"}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (activeCalculator === "GSTCalc") {
        const gstData = chartData["GSTCalc"];
        const colors = ["#57C6F4", "#FF8566"];
        if (!gstData || gstData.length === 0) return null;

        return (
          <>
            {gstData?.map((item, index) => {
              const isLastItem = index === gstData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}{" "}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}{" "}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (activeCalculator === "VatCalc") {
        const vatData = chartData["VatCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {vatData?.map((item, index) => {
              const isLastItem = index === vatData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}{" "}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}{" "}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "pensionCalc") {
        const pensionData = chartData["pensionCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {pensionData?.map((item, index) => {
              return (
                <React.Fragment key={item.label}>
                  <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0">
                    <div className="flex items-center">
                      <div
                        className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                          }`}
                        style={{ backgroundColor: colors[index] }}
                      ></div>
                      <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                        {textData["chartLabels"][item.label]}
                      </div>
                    </div>

                    <div className="text-sm sm:text-sm text-black font-medium whitespace-nowrap">
                      {textData.currency}{" "}
                      {typeof item.value === "number" ||
                        !isNaN(parseFloat(item.value))
                        ? FormatIndianNumber(parseFloat(item.value))
                        : "0"}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "loanToValueCalc") {
        const ltvData = chartData["loanToValueCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {ltvData?.map((item, index) => {
              const isLastItem = index === ltvData.length - 1;

              return (
                <React.Fragment key={item.label}>
                  {!isLastItem && (
                    <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0">
                      <div className="flex items-center">
                        <div
                          className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                            }`}
                          style={{ backgroundColor: colors[index] }}
                        ></div>
                        <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                          {textData["chartLabels"][item.label]}
                        </div>
                      </div>

                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}{" "}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}

                  {isLastItem && (
                    <div className="mt-5 sm:mt-6 bg-gray-100 rounded-lg flex justify-between">
                      <div className="text-sm sm:text-sm text-black">
                        {textData["chartLabels"][item.label]}
                      </div>
                      <div className="text-sm sm:text-sm text-black font-medium">
                        {textData.currency}{" "}
                        {typeof item.value === "number" ||
                          !isNaN(parseFloat(item.value))
                          ? FormatIndianNumber(parseFloat(item.value))
                          : "0"}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "contributionCalc") {
        const contributionData = chartData["contributionCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {contributionData?.map((item, index) => {
              return (
                <React.Fragment key={item.label}>
                  <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0">
                    <div className="flex items-center">
                      <div
                        className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                          }`}
                        style={{ backgroundColor: colors[index] }}
                      ></div>
                      <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                        {textData["chartLabels"][item.label]}
                      </div>
                    </div>

                    <div className="text-sm sm:text-sm text-black font-medium">
                      {textData.currency}{" "}
                      {typeof item.value === "number" ||
                        !isNaN(parseFloat(item.value))
                        ? FormatIndianNumber(parseFloat(item.value))
                        : "0"}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        );
      } else if (calculatorId === "mortgageCalc") {
        const mortgageData = chartData["mortgageCalc"];
        const colors = ["#57C6F4", "#FF8566"];

        return (
          <>
            {mortgageData?.map((item, index) => {
              return (
                <React.Fragment key={item.label}>
                  <div className="flex justify-between items-center pb-4 mt-5 sm:mt-6 sm:pb-4 border-b-[1px] border-gray-light last:border-b-0">
                    <div className="flex items-center">
                      <div
                        className={`h-[20px] w-[20px] rounded-[4px]  ${isRTL() ? "ml-[12px]" : "mr-[12px]"
                          }`}
                        style={{ backgroundColor: colors[index] }}
                      ></div>
                      <div className="text-sm sm:text-sm text-black font-lato-regular-400 text-left">
                        {textData["chartLabels"][item.label]}
                      </div>
                    </div>

                    <div className="text-sm sm:text-sm text-black font-medium">
                      {textData.currency}{" "}
                      {typeof item.value === "number" ||
                        !isNaN(parseFloat(item.value))
                        ? FormatIndianNumber(parseFloat(item.value))
                        : "0"}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        );
      }
    };

    return (
      <>
        <div className="header">
          {showDurationDropdown && (
            <div className="mb-[40px]">
              <div
                className="relative inline-block text-left w-full "
                ref={dropdownRef}
              >
                <button
                  onClick={toggleDropdown}
                  type="button"
                  className="h-[44px] px-[16px] py-[12px] drop-shadow-[0px_0px_15px_rgba(152,167,167,0.08)] inline-flex justify-between w-full items-center rounded-[102px]    bg-white sm:text-sm text-nowrap text-sm font-lato-regular-400  focus:outline-none  focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                >
                  {selectedOption || "Durations of yearly return"}
                  <svg
                    className={`-mr-1 ml-2 h-5 w-5 transition duration-300 ease-in-out ${isOpen ? "transform rotate-180" : ""
                      }`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {isOpen && (
                  <div className="  drop-shadow-[0px_0px_15px_rgba(152,167,167,0.08)] origin-top-right absolute right-0 mt-2 w-full rounded-[12px] shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                    <div
                      className="py-1  "
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="options-menu"
                    >
                      {generateDropdownOptions(noOfInvestementYear)?.map(
                        (option, index) => (
                          <button
                            key={option.value}
                            onClick={() => handleOptionClick(option.value)}
                            className={`block px-4 py-2 w-full  text-left text-sm sm:text-sm text-gray transition duration-300 ease-in-out`}
                            role="menuitem"
                          >
                            <p
                              className={`${index === 2 ? "" : "pb-2 border-b"
                                } border-gray-light w-full`}
                            >
                              {option.label}
                            </p>
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {calculatorId === "personalLoanCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.emis}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold">
                {textData.currency}{" "}
                {perEmi
                  ? Math.round(perEmi).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language === "fa"
                        ? "en-IN"
                        : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}
          {calculatorId === "FDCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.totalValue}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold ">
                {textData.currency}{" "}
                {sumAmount
                  ? Math.round(sumAmount).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}

          {calculatorId === "RDCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.totalValue}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold ">
                {textData.currency}{" "}
                {totalValue
                  ? Math.round(totalValue).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}

          {calculatorId === "educationLoanCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.emis}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold ">
                {textData.currency}{" "}
                {emi
                  ? Math.round(emi).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}

          {calculatorId === "creditCardPayoffCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.noOfmonth}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {monthsToPayoff}
              </span>
            </div>
          )}

          {calculatorId === "businessLoanCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm  font-normal">
                {" "}
                {textData.chartLabels.emis}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {textData.currency}{" "}
                {bussinessEmi
                  ? Math.round(bussinessEmi).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}

          {calculatorId === "earlyLoanCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.newEMi}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {textData.currency}{" "}
                {newEmi
                  ? Math.round(newEmi).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}

          {calculatorId === "DTICalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.debtRatio}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {dtiRatio ? dtiRatio.toFixed(2) : "0"}%
              </span>
            </div>
          )}

          {calculatorId === "netWorthCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.yourNetworth}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {textData.currency}{" "}
                {netWorth
                  ? Math.round(netWorth).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}

          {calculatorId === "salaryCalc" && (
            <div>
              <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-5 sm:mb-6 text-center">
                <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                  {textData.chartLabels.anualSalary}
                </h3>
                <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                  {textData.currency}{" "}
                  {annualSalary
                    ? Math.round(annualSalary).toLocaleString(
                      i18n.language === "fa"
                        ? "en-IN"
                        : i18n.language || "en-IN"
                    )
                    : "0"}
                </span>
              </div>
              <div className="flex justify-between">
                <h4 className="text-sm  "> {textData.chartLabels.totalHour}</h4>
                <span className="text-black text-sm   font-medium ml-1 text-start">
                  {totalWorkingHours
                    ? Math.round(totalWorkingHours).toLocaleString(
                      i18n.language === "fa"
                        ? "en-IN"
                        : i18n.language || "en-IN"
                    )
                    : "0"}
                </span>
              </div>
            </div>
          )}

          {calculatorId === "pensionCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.monthlyPension}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {textData.currency}{" "}
                {monthlyPension
                  ? Math.round(monthlyPension).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}

          {calculatorId === "loanToValueCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {textData.chartLabels.emis}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {textData.currency}{" "}
                {monthlyEmi
                  ? Math.round(monthlyEmi).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}
          {calculatorId === "savingCalc" && (
            <div>
              <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-4 text-center">
                <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                  {textData.chartLabels.monthlyInvestment}
                </h3>
                <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                  {textData.currency}{" "}
                  {monthlyInvestment
                    ? Math.round(monthlyInvestment).toLocaleString(
                      i18n.language === "fa"
                        ? "en-IN"
                        : i18n.language || "en-IN"
                    )
                    : "0"}
                </span>
              </div>
              <div className="border-b border-gray-light "></div>

              <div className="flex justify-between py-6 text-start">
                <h4 className="text-sm  ">
                  {" "}
                  {textData.chartLabels.futureValue}
                </h4>
                <span>
                  <span className="text-black text-sm  font-medium whitespace-nowrap">
                    {" "}
                    {textData.currency}{" "}
                    {futureValueOfGoal
                      ? Math.round(futureValueOfGoal).toLocaleString(
                        i18n.language === "fa"
                          ? "en-IN"
                          : i18n.language || "en-IN"
                      )
                      : "0"}
                  </span>
                </span>
              </div>
              <div className="flex justify-between text-start">
                <h4 className="text-sm ">
                  {" "}
                  {textData.chartLabels.existingInvestment}
                </h4>

                <span className="text-black text-sm  font-medium whitespace-nowrap">
                  {" "}
                  {textData.currency}{" "}
                  {futureValueOfExistingInvestment
                    ? Math.round(
                      futureValueOfExistingInvestment
                    ).toLocaleString(
                      i18n.language === "fa"
                        ? "en-IN"
                        : i18n.language || "en-IN"
                    )
                    : "0"}
                </span>
              </div>
            </div>
          )}

          {calculatorId === "pensionCalc" && shouldRenderBarChart && (
            <>
              <PensionBar
                totalInvestment={pensionResult.totalInvestment}
                totalCorpus={pensionResult.totalCorpus}
              />
            </>
          )}
          {calculatorId === "contributionCalc" &&
            shouldRenderContributionBarChart && (
              <>
                <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
                  <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                    {textData.chartLabels.monthlyRetiremnet}
                  </h3>
                  <span className="text-primary text-base sm:text-[17px] font-extrabold break-words">
                    {textData.currency}{" "}
                    {monthlyRate ? formatNumberShort(monthlyRate,
                      langCode
                    ) : "0"}

                  </span>
                </div>
                <ContributionChart />
              </>
            )}

          {calculatorId === "carCalc" && shouldRenderCarBarChart && (
            <>
              <CarBarChart
                carBuyCost={chartData["carCalc"][0]?.value || 0}
                carLeaseCost={chartData["carCalc"][1]?.value || 0}
                purchasePrice={carData?.purchasePrice || 0}
                monthlyBuyPayment={carData?.monthlyBuyPayment || 0}
                totalInterest={carData?.totalInterest || 0}
                totalLeasePayments={carData?.totalLeasePayments || 0}
                securityDeposit={carData?.securityDeposit || 0}
                downPayment={carData?.downPayment || 0}
              />
            </>
          )}

          {calculatorId === "mortgageCalc" && (
            <>
              <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
                <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                  {textData.chartLabels.newMonthlyamt}
                </h3>
                <span className="text-primary text-base sm:text-[17px] font-extrabold">
                  {textData.currency}{" "}
                  {newAmount
                    ? Math.round(newAmount).toLocaleString(
                      i18n.language === "fa"
                        ? "en-IN"
                        : i18n.language || "en-IN"
                    )
                    : "0"}
                </span>
              </div>
              <MortgageChart />
            </>
          )}

          {activeCalculator === "GSTCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {" "}
                {textData.chartLabels.totalGst}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {textData.currency}{" "}
                {gstValue
                  ? Math.round(gstValue).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}
          {activeCalculator === "VatCalc" && (
            <div className="bg-white shadow-custom-soft rounded-[20px] py-3 px-5 mb-8 text-center">
              <h3 className="text-black text-sm sm:text-sm font-normal pb-[10px] text-center">
                {" "}
                {textData.chartLabels.totalVat}
              </h3>
              <span className="text-primary text-base sm:text-[17px] font-extrabold pt-5">
                {textData.currency}{" "}
                {vatValue
                  ? Math.round(vatValue).toLocaleString(
                    i18n.language === "fa"
                      ? "en-IN"
                      : i18n.language || "en-IN"
                  )
                  : "0"}
              </span>
            </div>
          )}

          {calculatorId !== "salaryCalc" &&
            calculatorId !== "contributionCalc" &&
            calculatorId !== "carCalc" &&
            calculatorId !== "savingCalc" &&
            calculatorId !== "mortgageCalc" &&
            data && (
              <>
                <div
                  id="legend"
                  className="sm:w-[220px] sm:h-[220px] w-[220px] h-[220px] m-auto"
                >
                  <Doughnut data={data} options={options} />
                </div>
                <div>{renderChartLegends()}</div>
              </>
            )}
        </div>
      </>
    );
  }
);

export default ChartComponent;
